import { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import './styles/individualJourney.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import { MONTHS, time, groupByDay } from './lessonDates';
import BookingCalendar from './BookingCalendar';

type TermOption = { id: string; weekday: number; time: string; voted: boolean };
export type Journey = {
    status: number;
    assignedWeekday: number | null;
    assignedTime: string | null;
    firstLectureDate: string | null;
    termOptions: TermOption[];
};
type Lesson = { id: string; start: string; durationMinutes: number; topic: string; meetUrl: string; recordingUrl: string | null; isPast: boolean };

// 0 = Monday … 6 = Sunday (matches the backend convention).
const WEEKDAYS = ["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];

const fmtTerm = (w: number, t: string) => WEEKDAYS[w] + " v " + t;
const fmtDate = (s: string) => { const d = new Date(s); return d.getDate() + ". " + MONTHS[d.getMonth()] + " " + d.getFullYear() + " v " + time(s); };

export default function IndividualJourney({ journey, onChanged }: { journey: Journey; onChanged: () => void }) {
    const api = new ApiHelper();
    const alert = useAlert();

    const [schedule, setSchedule] = useState<Lesson[]>([]);

    useEffect(() => {
        if (journey.status === 2) fetchSchedule();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journey.status]);

    async function fetchSchedule() {
        try {
            const res = await api.onlineLessonsApi.getScheduleList();
            const data = await readJson(res);
            setSchedule(data.upcoming ?? []);
        } catch (x: any) { api.processErrors(x); }
    }

    async function vote(optionId: string, voted: boolean) {
        try {
            await api.onlineLessonsApi.voteCreate(optionId);
            alert.success(voted ? "Termín byl odebrán" : "Termín byl uložen");
            onChanged();
        } catch (x: any) { api.processErrors(x); }
    }

    // -1 — Welcome: lecture product bought, but the admin hasn't placed the
    // student in a group yet. Greet them and promise term voting soon; they can
    // already book extra individual lessons below in the meantime.
    if (journey.status === -1) {
        return (
            <div className="journey">
                <section className="jHero">
                    <span className="eyebrow">Online lekce</span>
                    <h1>Vítej v kurzu!</h1>
                    <p>Děkujeme za nákup. Brzy si budeš moci zvolit termín, který ti nejvíc vyhovuje — podle hlasování ti přiřadíme pravidelný čas online lekcí s lektorem.</p>
                </section>
                <BookingCalendar />
            </div>
        );
    }

    // 0 — Term voting: pick a preferred term.
    if (journey.status === 0) {
        return (
            <div className="journey">
                <section className="jHero">
                    <span className="eyebrow">Individuální výuka</span>
                    <h1>Vyber si termín výuky</h1>
                    <p>Zvol termíny, které ti vyhovují. Podle hlasování ti přiřadíme pravidelný čas lekcí s lektorem.</p>
                </section>
                <section className="jCard">
                    <h2>Možné termíny</h2>
                    <p className="sub">Označ všechny termíny, které ti vyhovují — můžeš jich vybrat víc.</p>
                    {journey.termOptions.length === 0 ?
                        <p className="empty">Termíny k hlasování zatím nejsou připravené. Brzy se tu objeví.</p>
                        :
                        <div className="options">
                            {journey.termOptions.map(o => (
                                <div key={o.id} className={"option" + (o.voted ? " voted" : "")} onClick={() => vote(o.id, o.voted)}>
                                    <span className="day">{fmtTerm(o.weekday, o.time)}</span>
                                    {o.voted && <span className="check">✓ zvoleno</span>}
                                </div>
                            ))}
                        </div>
                    }
                </section>
                <BookingCalendar />
            </div>
        );
    }

    // 1 — Waiting to start: info placeholder (more will be added later).
    if (journey.status === 1) {
        const hasTerm = journey.assignedWeekday != null && journey.assignedTime;
        return (
            <div className="journey">
                <section className="jHero">
                    <span className="eyebrow">Individuální výuka</span>
                    <h1>Výuka brzy začne</h1>
                    <p>Děkujeme! Tvůj kurz se připravuje. Jakmile výuka odstartuje, najdeš tady rozvrh lekcí a odkazy na připojení.</p>
                    {journey.firstLectureDate && <span className="term">První přednáška: {fmtDate(journey.firstLectureDate)}</span>}
                    {hasTerm && <span className="term">Pravidelný termín: {fmtTerm(journey.assignedWeekday!, journey.assignedTime!)}</span>}
                </section>
                <section className="jCard">
                    <h2>Co bude dál</h2>
                    <p className="empty">Tady postupně přibydou další informace k tvé přípravě. Mezitím můžeš procházet studijní materiály.</p>
                </section>
                <BookingCalendar />
            </div>
        );
    }

    // 2 — Lectures running: the lesson calendar.
    if (journey.status === 2) {
        const days = groupByDay(schedule);
        return (
            <div className="journey">
                <section className="jHero">
                    <span className="eyebrow">Individuální výuka</span>
                    <h1>Tvůj rozvrh</h1>
                    <p>Tady najdeš nadcházející lekce s lektorem. Připoj se jedním klikem v čase lekce.</p>
                    {journey.assignedWeekday != null && journey.assignedTime &&
                        <span className="term">Pravidelný termín: {fmtTerm(journey.assignedWeekday, journey.assignedTime)}</span>}
                </section>
                <section className="jCard">
                    <h2>Nadcházející lekce</h2>
                    {days.length === 0 ?
                        <p className="empty">Zatím nejsou naplánované žádné lekce. Jakmile lektor termín přidá, objeví se tady.</p>
                        :
                        <div className="calendar">
                            {days.map((g, gi) => (
                                <div key={gi} className="calDay">
                                    <div className="dateBox"><span className="d">{g.day}.</span><span className="m">{g.month}</span></div>
                                    <div className="items">
                                        {g.items.map(l => (
                                            <div key={l.id} className="item">
                                                <div>
                                                    <span className="time">{time(l.start)} · {l.durationMinutes} min</span>
                                                    <h3>{l.topic || "Individuální lekce"}</h3>
                                                </div>
                                                {l.meetUrl
                                                    ? <button className="btn join" onClick={() => window.open(l.meetUrl, "_blank")}>Připojit</button>
                                                    : <span className="empty">Odkaz bude doplněn</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                </section>
                <BookingCalendar />
            </div>
        );
    }

    // 3 — Lectures done: good luck + booking calendar for extra lectures.
    return (
        <div className="journey">
            <section className="jHero">
                <span className="eyebrow">Individuální výuka</span>
                <h1>Hodně štěstí u testu! 🍀</h1>
                <p>Tvoje příprava je u konce — odvedl(a) jsi skvělou práci. Věříme, že to u přijímaček zvládneš na výbornou.</p>
            </section>
            <BookingCalendar />
        </div>
    );
}
